(() => {
  const fallbackRecs = window.YY_CART_RECS || [];
  const variantLimits = window.YY_CART_VARIANT_LIMITS || {};
  const cart = {
    items: [],
    recs: fallbackRecs,
    loading: false,
    message: '',
  };
  const itemBgs = ['#4DDDB0', '#FFC83D', '#B89BFF', '#C8F04A', '#F4ECDB'];
  const bgForVariant = id => itemBgs[Math.abs(Number(id) || 0) % itemBgs.length];

  const money = cents => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(value);
  };

  const escapeHtml = value => String(value || '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(options.headers || {}),
      },
      ...options,
    });
    if (!response.ok) {
      let message = `Cart request failed: ${response.status}`;
      try {
        const error = await response.json();
        message = error.description || error.message || message;
      } catch (_) {}
      throw new Error(message);
    }
    return response.json();
  };

  const normalizeCart = data => {
    cart.items = (data.items || []).map((item, index) => {
      const limit = variantLimits[String(item.variant_id)] || null;
      const hasLimit = limit && String(limit.policy || '').toLowerCase() !== 'continue';
      const maxQty = hasLimit ? Math.max(0, Number(limit.quantity) || 0) : null;
      return {
        id: item.key,
        line: index + 1,
        variantId: item.variant_id,
        name: item.product_title || item.title,
        meta: item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : 'Ready to ship',
        unit: item.final_price,
        linePrice: item.final_line_price,
        qty: item.quantity,
        maxQty,
        img: item.image || '',
        url: item.url || '#',
        bg: bgForVariant(item.variant_id),
      };
    });
  };

  const totalQty = () => cart.items.reduce((sum, item) => sum + item.qty, 0);
  const subtotal = () => cart.items.reduce((sum, item) => sum + item.linePrice, 0);

  const stockLine = item => Number.isFinite(item.maxQty)
    ? `${item.maxQty} in stock`
    : 'Ready to ship';

  const itemMarkup = item => `
    <article class="cart-drawer__item" data-cart-item="${escapeHtml(item.id)}" style="--card-bg:${item.bg}">
      <a class="cart-drawer__ph" href="${escapeHtml(item.url)}">
        ${item.img ? `<img src="${escapeHtml(item.img)}" alt="">` : ''}
      </a>
      <div class="cart-drawer__info">
        <div class="cart-drawer__line">
          <a class="cart-drawer__name" href="${escapeHtml(item.url)}">${escapeHtml(item.name)}</a>
          <span class="cart-drawer__price">${money(item.linePrice)}</span>
        </div>
        <div class="cart-drawer__meta">${escapeHtml(stockLine(item))}</div>
        <div class="cart-drawer__tools">
          <div class="cart-drawer__qty" aria-label="Quantity for ${escapeHtml(item.name)}">
            <button type="button" data-cart-line="${item.line}" data-delta="-1" aria-label="Decrease quantity">−</button>
            <span>${item.qty}</span>
            <button type="button" data-cart-line="${item.line}" data-delta="1" ${Number.isFinite(item.maxQty) && item.qty >= item.maxQty ? 'disabled aria-disabled="true" title="No more inventory available"' : ''} aria-label="Increase quantity">+</button>
          </div>
          <button class="cart-drawer__remove" type="button" data-cart-remove="${escapeHtml(item.id)}" aria-label="Remove ${escapeHtml(item.name)}">
            <span aria-hidden="true">×</span>
          </button>
        </div>
      </div>
    </article>
  `;

  const recMarkup = item => `
    <article class="cart-drawer__rec" style="--card-bg:${item.bg || 'var(--sun)'}">
      <a class="cart-drawer__rec-img" href="${escapeHtml(item.url || '#')}">${item.img ? `<img src="${escapeHtml(item.img)}" alt="" crossorigin="anonymous">` : ''}</a>
      <p>${escapeHtml(item.name)}</p>
      <div>
        <span class="cart-drawer__rec-price">${money(item.price)}</span>
        <button type="button" data-cart-add-rec="${escapeHtml(item.variantId)}">Add</button>
      </div>
    </article>
  `;

  const alphaBounds = img => {
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!width || !height) return null;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0);
    const data = ctx.getImageData(0, 0, width, height).data;
    let minX = width, minY = height, maxX = -1, maxY = -1;
    for (let y = 0; y < height; y += 1) {
      for (let x = 0; x < width; x += 1) {
        const alpha = data[((y * width + x) * 4) + 3];
        if (alpha > 12) {
          if (x < minX) minX = x;
          if (y < minY) minY = y;
          if (x > maxX) maxX = x;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < minX || maxY < minY) return null;
    return { x: minX, y: minY, width: maxX - minX + 1, height: maxY - minY + 1 };
  };

  const normalizeRecImages = () => {
    document.querySelectorAll('.cart-drawer__rec-img img').forEach(img => {
      const box = img.closest('.cart-drawer__rec-img');
      if (!box) return;
      const apply = () => {
        try {
          const bounds = alphaBounds(img);
          if (!bounds) return;
          const boxRect = box.getBoundingClientRect();
          const fit = Math.min(boxRect.width / img.naturalWidth, boxRect.height / img.naturalHeight);
          const visibleW = bounds.width * fit;
          const visibleH = bounds.height * fit;
          const scale = Math.min((boxRect.width * 1.12) / visibleW, (boxRect.height * 1.22) / visibleH);
          const renderedW = bounds.width * fit * scale;
          const renderedH = bounds.height * fit * scale;
          const x = (boxRect.width - renderedW) / 2 - bounds.x * fit * scale;
          const y = (boxRect.height - renderedH) * 0.16 - bounds.y * fit * scale;
          img.style.setProperty('--rec-alpha-scale', scale.toFixed(4));
          img.style.setProperty('--rec-alpha-x', `${x.toFixed(2)}px`);
          img.style.setProperty('--rec-alpha-y', `${y.toFixed(2)}px`);
        } catch (_) {}
      };
      if (img.complete && img.naturalWidth) apply();
      else img.addEventListener('load', apply, { once: true });
    });
  };

  const render = () => {
    document.querySelectorAll('[data-cart-count], .cart-count').forEach(el => {
      el.textContent = totalQty();
    });
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;

    const count = totalQty();
    drawer.querySelector('[data-cart-items-count]').textContent = count;
    const status = drawer.querySelector('[data-cart-status]');
    if (status) {
      status.textContent = cart.message;
      status.hidden = !cart.message;
    }
    drawer.querySelector('[data-cart-list]').innerHTML = cart.items.length
      ? cart.items.map(itemMarkup).join('')
      : '<div class="cart-drawer__empty">Your bag is taking a tiny nap.</div>';
    drawer.querySelector('[data-cart-subtotal]').textContent = money(subtotal());
    drawer.querySelector('[data-cart-remove-all]').disabled = cart.items.length === 0 || cart.loading;
    drawer.querySelector('.cart-drawer__recs').innerHTML = cart.recs.map(recMarkup).join('');
    requestAnimationFrame(normalizeRecImages);
  };

  const refreshCart = async () => {
    try {
      const data = await fetchJson('/cart.js');
      normalizeCart(data);
    } catch (error) {
      cart.items = [];
    }
    render();
  };

  const addToCart = async (variantId, quantity = 1) => {
    if (!variantId || cart.loading) return;
    cart.loading = true;
    cart.message = '';
    render();
    try {
      await fetchJson('/cart/add.js', {
        method: 'POST',
        body: JSON.stringify({ id: Number(variantId), quantity: Math.max(1, Number(quantity) || 1) }),
      });
      await refreshCart();
      setOpen(true);
    } catch (error) {
      cart.message = error.message || 'Could not update cart.';
    } finally {
      cart.loading = false;
      render();
    }
  };

  const changeLine = async (line, quantity) => {
    if (!line || cart.loading) return;
    cart.loading = true;
    cart.message = '';
    render();
    try {
      const data = await fetchJson('/cart/change.js', {
        method: 'POST',
        body: JSON.stringify({ line: Number(line), quantity: Math.max(0, Number(quantity) || 0) }),
      });
      normalizeCart(data);
    } catch (error) {
      cart.message = error.message || 'Could not update cart.';
    } finally {
      cart.loading = false;
      render();
    }
  };

  const clearCart = async () => {
    if (cart.loading) return;
    cart.loading = true;
    render();
    try {
      const data = await fetchJson('/cart/clear.js', { method: 'POST', body: '{}' });
      normalizeCart(data);
    } finally {
      cart.loading = false;
      render();
    }
  };

  if (!document.getElementById('cart-drawer')) {
    const drawer = document.createElement('aside');
    drawer.className = 'cart-drawer';
    drawer.id = 'cart-drawer';
    drawer.setAttribute('aria-hidden', 'true');
    drawer.innerHTML = `
      <div class="cart-drawer__head">
        <div class="cart-drawer__title-row">
          <span class="cart-drawer__label">Cart</span>
          <span class="cart-drawer__bubble" data-cart-items-count>${totalQty()}</span>
        </div>
        <button class="cart-drawer__close" type="button" data-cart-close aria-label="Close cart">×</button>
      </div>
      <div class="cart-drawer__status" data-cart-status hidden></div>
      <div class="cart-drawer__body">
        <div class="cart-drawer__list" data-cart-list></div>
        <button class="cart-drawer__remove-all" type="button" data-cart-remove-all>Remove all</button>

        <section class="cart-drawer__likes" aria-label="You may also like">
          <div class="cart-drawer__section-head">
            <h3>You may also like</h3>
            <div class="cart-drawer__rec-controls" aria-label="Recommended products controls">
              <button type="button" data-cart-rec-scroll="-1" aria-label="Previous recommended products">←</button>
              <button type="button" data-cart-rec-scroll="1" aria-label="Next recommended products">→</button>
            </div>
          </div>
          <div class="cart-drawer__recs"></div>
        </section>

        <details class="cart-drawer__note">
          <summary>Add order note</summary>
          <textarea maxlength="140" placeholder="Gift note, delivery detail, tiny request..."></textarea>
        </details>
      </div>
      <div class="cart-drawer__foot">
        <div class="cart-drawer__total">
          <span>Subtotal</span>
          <strong data-cart-subtotal>${money(subtotal())}</strong>
        </div>
        <p class="cart-drawer__tax">Taxes and shipping calculated at checkout</p>
        <a class="cart-drawer__checkout" href="/checkout">Checkout</a>
        <a class="cart-drawer__view" href="${escapeHtml(window.YY_CART_URL || '/cart')}">View cart</a>
      </div>
    `;
    const backdrop = document.createElement('button');
    backdrop.className = 'cart-drawer-backdrop';
    backdrop.type = 'button';
    backdrop.setAttribute('data-cart-close', '');
    backdrop.setAttribute('aria-label', 'Close cart');
    document.body.append(backdrop, drawer);
  }

  function setOpen(open) {
    document.body.classList.toggle('cart-drawer-open', open);
    const drawer = document.getElementById('cart-drawer');
    if (drawer) drawer.setAttribute('aria-hidden', open ? 'false' : 'true');
    document.querySelectorAll('[data-cart-toggle]').forEach(btn => {
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  }

  document.addEventListener('click', event => {
    const addButton = event.target.closest('[data-cart-add]');
    if (addButton) {
      event.preventDefault();
      event.stopPropagation();
      const qtySource = addButton.dataset.cartQuantitySource;
      const quantity = qtySource ? document.getElementById(qtySource)?.textContent : addButton.dataset.cartQty;
      addButton.classList.remove('bump');
      void addButton.offsetWidth;
      addButton.classList.add('bump');
      addToCart(addButton.dataset.cartAdd, quantity || 1).catch(console.error);
      return;
    }

    const toggle = event.target.closest('[data-cart-toggle]');
    if (toggle) {
      event.preventDefault();
      refreshCart().finally(() => setOpen(true));
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      setOpen(false);
      return;
    }

    const qtyButton = event.target.closest('[data-cart-line]');
    if (qtyButton) {
      event.preventDefault();
      event.stopPropagation();
      if (qtyButton.disabled) return;
      const item = cart.items.find(entry => String(entry.line) === String(qtyButton.dataset.cartLine));
      if (!item) return;
      changeLine(item.line, item.qty + Number(qtyButton.dataset.delta)).catch(console.error);
      return;
    }

    const removeButton = event.target.closest('[data-cart-remove]');
    if (removeButton) {
      const item = cart.items.find(entry => entry.id === removeButton.dataset.cartRemove);
      changeLine(item?.line, 0).catch(console.error);
      return;
    }

    if (event.target.closest('[data-cart-remove-all]')) {
      clearCart().catch(console.error);
      return;
    }

    const recScrollButton = event.target.closest('[data-cart-rec-scroll]');
    if (recScrollButton) {
      const recs = document.querySelector('.cart-drawer__recs');
      if (!recs) return;
      recs.scrollBy({
        left: Number(recScrollButton.dataset.cartRecScroll) * Math.round(recs.clientWidth * 0.72),
        behavior: 'smooth',
      });
      return;
    }

    const recButton = event.target.closest('[data-cart-add-rec]');
    if (recButton) {
      addToCart(recButton.dataset.cartAddRec, 1).catch(console.error);
    }
  });

  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') setOpen(false);
  });

  refreshCart();
})();
